import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './module_roots/AuthContext';
import PrivateRoute from './module_roots/PrivateRoute';

import DashboardBusinessManager from './module_roots/DashboardBusinessManager';
import Login from './module_roots/Login';
import LedgerView from './module_cosmetics/LedgerView';
import SellList from './module_fabrics/Fab_SaleList';
import RootControlCenter from './module_roots/RootControlCenter';
import OurZestLoader from './module_roots/OurZestLoader';
import './styles/AppStartup.css';

const STARTUP_SPLASH_KEY = 'ourzest_startup_splash_seen_v1';
let startupSplashActive = false;

const isSuperAdminRole = (role) => ['super_admin', 'superadmin'].includes(String(role || '').toLowerCase());

const homeForUser = (user) => (
  isSuperAdminRole(user?.role) ? '/DashboardSuperAdmin' : '/Dashboard-Business-Manager'
);

function StartupSplashGate({ children }) {
  const [showSplash, setShowSplash] = useState(() => {
    try {
      // sessionStorage survives refreshes in this tab. The logo splash is
      // therefore shown once when the app starts, never on route changes or
      // later refreshes.
      if (startupSplashActive) return true;
      if (sessionStorage.getItem(STARTUP_SPLASH_KEY)) return false;
      sessionStorage.setItem(STARTUP_SPLASH_KEY, '1');
      startupSplashActive = true;
      return true;
    } catch {
      return !startupSplashActive;
    }
  });

  useEffect(() => {
    if (!showSplash) return undefined;
    const timer = window.setTimeout(() => {
      startupSplashActive = false;
      setShowSplash(false);
    }, 3000);
    return () => window.clearTimeout(timer);
  }, [showSplash]);

  return showSplash ? <OurZestLoader startup /> : children;
}

function RouteLoading() {
  return <div className="oz-route-pending" role="status" aria-label="Checking session" />;
}

// Never display the login form over an already authenticated session.
function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <RouteLoading />;
  return user ? <Navigate to={homeForUser(user)} replace /> : <Login />;
}

// An unknown database menu URL must not be treated as logout. Authenticated
// users return to their workspace; unauthenticated visitors go to login.
function SafeFallbackRoute() {
  const { user, loading } = useAuth();
  if (loading) return <RouteLoading />;
  return <Navigate to={user ? homeForUser(user) : '/login'} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LoginRoute />} />
      <Route path="/login" element={<LoginRoute />} />

      <Route
        path="/ledger"
        element={(
          <PrivateRoute>
            <LedgerView />
          </PrivateRoute>
        )}
      />

      <Route
        path="/DashboardSuperAdmin"
        element={(
          <PrivateRoute allowedRoles={['super_admin', 'superadmin']}>
            <RootControlCenter />
          </PrivateRoute>
        )}
      />
      <Route
        path="/DashboardSuperAdmin/*"
        element={(
          <PrivateRoute allowedRoles={['super_admin', 'superadmin']}>
            <RootControlCenter />
          </PrivateRoute>
        )}
      />

      <Route
        path="/Dashboard-Business-Manager/*"
        element={(
          <PrivateRoute>
            <DashboardBusinessManager />
          </PrivateRoute>
        )}
      />

      <Route
        path="/dashboard/fab-sales"
        element={(
          <PrivateRoute>
            <SellList />
          </PrivateRoute>
        )}
      />

      <Route path="*" element={<SafeFallbackRoute />} />
    </Routes>
  );
}

export default function App() {
  return (
    <StartupSplashGate>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </StartupSplashGate>
  );
}
